/* eslint-disable react-hooks/exhaustive-deps */
import clsx from 'clsx'
import styled from '@emotion/styled'
import React, {FC, useEffect, useMemo, useState} from 'react'
import {Link} from 'react-router-dom'
import {KTSVG, toAbsoluteUrl} from '../../../helpers'
import {useLayout} from '../../core'
import {Header} from './Header'
import {DefaultTitle} from './page-title/DefaultTitle'
import {Topbar} from './Topbar'
import * as API from '../../../../app/api'
import {getUserData} from '../../../../app/modules/auth'
import {useSidebar} from '../../../../app/components/sidebar/SidebarContext'
import Select, {components, OptionProps} from 'react-select'

const companyTypeFilters = [
  {value: 'distribuidor', label: 'Distribuidor'},
  {value: 'industria', label: 'Indústria'},
  {value: 'lojista', label: 'Lojista'},
] as const

type CompanyTypeFilter = typeof companyTypeFilters[number]['value']

type CompanyTypeOption = {
  value: CompanyTypeFilter | 'all'
  label: string
}

const companyTypeFilterOptions: CompanyTypeOption[] = [
  {value: 'all', label: 'Todos os tipos'},
  ...companyTypeFilters,
]

const CompanyTypeFilterOption = (props: OptionProps<CompanyTypeOption, true>) => (
  <components.Option {...props}>
    <span className='d-flex align-items-center gap-2'>
      <input
        type='checkbox'
        className='form-check-input m-0'
        checked={props.isSelected}
        readOnly
        tabIndex={-1}
        aria-hidden='true'
        style={{pointerEvents: 'none', width: 16, height: 16, flexShrink: 0}}
      />
      {props.label}
    </span>
  </components.Option>
)

const normalizeCompanyType = (type: string) =>
  type
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLocaleLowerCase('pt-BR')

const StyledSelect = styled.div`
  display: flex;
  align-items: center;
  flex: 0 1 370px;
  padding: 7px;
  width: 370px;
  /* em telas estreitas, encolhe para não expulsar o restante do header */
  max-width: min(370px, calc(100vw - 200px));
  min-width: 0;
  position: relative;

  .topbar-select__control {
    height: 38px;
    min-height: 38px;
  }

  .topbar-select__value-container {
    flex-wrap: nowrap;
  }

  .topbar-select__placeholder {
    color: hsl(0, 0%, 20%);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export function HeaderWrapper() {
  const {config, classes, attributes} = useLayout()
  const {header, aside} = config
  const {openDrawer} = useSidebar()
  const [companys, setCompanys] = useState<any[]>([])
  const [company, setCompany] = useState<string>('')
  const [selectedCompanyTypes, setSelectedCompanyTypes] = useState<CompanyTypeFilter[]>([])

  useEffect(() => {
    if (!getUserData()?.is_superadmin) return
    const currentCompany = localStorage.getItem('current_company')
    localStorage.setItem(
      'current_company',
      currentCompany ? currentCompany : getUserData().company_guid
    )
    setCompany(localStorage.getItem('current_company') as string)
    API.listCompanys().then((response) => {
      response.data.sort(function (a: any, b: any) {
        if (a.name < b.name) return -1
        if (a.name > b.name) return 1
        return 0
      })
      const data = response.data.filter((company: any) => {
        return company.is_actived
      })
      setCompanys(data)
    })
  }, [])

  const filteredCompanyOptions = useMemo(
    () =>
      companys
        .filter(
          (company) =>
            !selectedCompanyTypes.length ||
            selectedCompanyTypes.some(
              (type) => type === normalizeCompanyType(company.company_type || '')
            )
        )
        .map((company) => ({
          value: company.guid,
          label: `${company.name} [${company.company_type}]`,
        })),
    [companys, selectedCompanyTypes]
  )

  const companyTypeFilterLabel =
    selectedCompanyTypes.length === 1
      ? companyTypeFilters.find((type) => type.value === selectedCompanyTypes[0])?.label
      : selectedCompanyTypes.length > 1
      ? `${selectedCompanyTypes.length} tipos selecionados`
      : 'Tipo de empresa'

  const selectedCompanyOption = useMemo(() => {
    const matchingCompany = companys.find((c) => c.guid === company)
    if (matchingCompany) {
      return {
        value: matchingCompany.guid,
        label: `${matchingCompany.name} [${matchingCompany.company_type}]`,
      }
    }
  }, [companys, company])

  return (
    <div
      id='kt_header'
      className={clsx('header', classes.header.join(' '), 'align-items-stretch')}
      {...attributes.headerMenu}
    >
      <div
        className={clsx(
          classes.headerContainer.join(' '),
          'd-flex align-items-stretch justify-content-between'
        )}
      >
        <DefaultTitle />
        {aside.display && (
          <div className='d-flex align-items-center d-lg-none ms-n3 me-1' title='Show aside menu'>
            <div
              className='btn btn-icon btn-active-light-primary w-30px h-30px w-md-40px h-md-40px'
              id='kt_aside_mobile_toggle'
              onClick={openDrawer}
            >
              <KTSVG path='/media/icons/duotune/abstract/abs015.svg' className='svg-icon-2x mt-1' />
            </div>
          </div>
        )}

        {!aside.display && (
          <div className='d-flex align-items-center flex-grow-1 flex-lg-grow-0'>
            <Link to='/dashboard' className='d-lg-none'>
              <img
                alt='Logo'
                src={toAbsoluteUrl('/media/logos/logo-wide.png')}
                className='h-30px'
              />
            </Link>
          </div>
        )}

        {aside.display && (
          <div className='d-flex align-items-center flex-grow-1 flex-lg-grow-0'>
            <Link to='/' className='d-lg-none'>
              <img
                alt='Logo'
                src={toAbsoluteUrl('/media/logos/logo-wide.png')}
                className='h-30px'
              />
            </Link>
          </div>
        )}

        <div
          className='d-flex align-items-stretch justify-content-between flex-grow-1'
          style={{minWidth: 0}}
        >
          {header.left === 'menu' && (
            <div className='d-flex align-items-stretch' id='kt_header_nav'>
              <Header />
            </div>
          )}

          {header.left === 'page-title' && (
            <div className='d-flex align-items-center' id='kt_header_nav'>
              <DefaultTitle />
            </div>
          )}

          <div className='d-flex align-items-stretch' style={{minWidth: 0}}>
            {companys?.length ? (
              <>
                <StyledSelect>
                  <Select<CompanyTypeOption, true>
                    className='form-select-lg w-100 form-select-solid form-item'
                    classNamePrefix='topbar-select'
                    inputId='company-type-filters'
                    aria-label='Filtrar empresas por tipo'
                    placeholder={companyTypeFilterLabel}
                    options={companyTypeFilterOptions}
                    value={companyTypeFilterOptions.filter((type) =>
                      type.value === 'all'
                        ? !selectedCompanyTypes.length
                        : selectedCompanyTypes.includes(type.value)
                    )}
                    components={{Option: CompanyTypeFilterOption}}
                    isMulti
                    isSearchable={false}
                    isClearable={false}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    controlShouldRenderValue={false}
                    onChange={(options, action) => {
                      setSelectedCompanyTypes(
                        action.option?.value === 'all'
                          ? []
                          : options.flatMap(({value}) => (value === 'all' ? [] : [value]))
                      )
                    }}
                  />
                </StyledSelect>
                <StyledSelect>
                  <Select
                    className='form-select-lg w-100 form-select-solid form-item'
                    classNamePrefix='topbar-select'
                    aria-label='Empresa acessada'
                    placeholder={'Selecione uma empresa'}
                    options={filteredCompanyOptions}
                    noOptionsMessage={() => 'Nenhuma empresa encontrada'}
                    value={selectedCompanyOption}
                    onChange={(c: any) => {
                      if (!c) return
                      setCompany(c.value)
                      localStorage.setItem('current_company', c.value)
                      if (document.location.href.indexOf('dashboard') != -1) {
                        document.location.href = '/home'
                      } else if (document.location.href.indexOf('alerts') != -1) {
                        document.location.href = '/home'
                      } else {
                        document.location.href = document.location.href
                      }
                    }}
                  />
                </StyledSelect>
              </>
            ) : (
              <></>
            )}

            <Topbar />
          </div>
        </div>
        {/* end::Wrapper */}
      </div>
    </div>
  )
}
